import { Landmark } from "lucide-react";
import React from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRub } from "@/lib/format";
import { ALL_ORGS, orgLabel } from "@/pages/cashflow/utils";
import type {
  CashflowOrg,
  CashflowReceivables,
  CashflowReceivablesOrg,
  CashflowSourceStatus,
} from "@/types/api";

/** Имя источника дебиторки на бэке (`cashflow/receivables.py`). */
const SOURCE = "receivables";

interface Props {
  receivables: CashflowReceivables | null;
  sources: CashflowSourceStatus[];
  orgs: CashflowOrg[];
  /** Вкладка страницы: `"*"` — все организации, иначе ИНН одной. */
  orgInn: string;
}

function OrgBlock({
  org,
  orgs,
  withHeading,
}: {
  org: CashflowReceivablesOrg;
  orgs: CashflowOrg[];
  withHeading: boolean;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        {withHeading && (
          <span className="text-sm font-medium">{orgLabel(org.org_inn, orgs)}</span>
        )}
        <span className="text-sm tabular-nums">{formatRub(org.total)}</span>
        <span className="text-xs text-muted-foreground">
          · строк регистра: {org.rows_count}
          {Number(org.intercompany_excluded) !== 0 && (
            <> · внутригрупповые передачи исключены: {formatRub(org.intercompany_excluded)}</>
          )}
        </span>
      </div>

      {org.by_doc_type.length === 0 ? (
        <p className="text-sm text-muted-foreground">Долга по регистру нет</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип документа</TableHead>
                <TableHead className="text-right">Строк</TableHead>
                <TableHead className="text-right">Долг</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {org.by_doc_type.map((d) => (
                <TableRow key={d.doc_type}>
                  <TableCell>{d.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{d.count}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatRub(d.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Контрагент (топ-10)</TableHead>
                <TableHead className="text-right">Долг</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {org.top_kontragents.map((k) => (
                <TableRow key={k.name}>
                  <TableCell>{k.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatRub(k.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Панель «Дебиторка (по регистру 1С)» — справка сбоку, не деньги календаря.
 *
 * Дебиторка — единственный деградируемый источник: если регистр не ответил,
 * бэк отдаёт `receivables: null` и строку `sources[]` со `status="failed"`.
 * Тогда панель пишет причину (а баннер той же строки рисует `AttentionPanel`) —
 * пустой или нулевой панели вместо ошибки быть не должно.
 */
export function ReceivablesPanel({
  receivables,
  sources,
  orgs,
  orgInn,
}: Props): React.ReactElement {
  const failed = sources.find((s) => s.name === SOURCE && s.status === "failed");
  const shown =
    receivables === null
      ? []
      : orgInn === ALL_ORGS
        ? receivables.orgs
        : receivables.orgs.filter((o) => o.org_inn === orgInn);
  const total =
    orgInn === ALL_ORGS ? receivables?.total : (shown[0]?.total ?? undefined);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Landmark className="h-4 w-4" />
          Дебиторка (по регистру 1С)
          {receivables !== null && total !== undefined && (
            <span className="text-sm font-normal tabular-nums">{formatRub(total)}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {receivables === null ? (
          <p className="text-sm text-rose-700">
            Дебиторка недоступна: {failed?.message ?? "причина не указана"}
          </p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            По выбранной организации ({orgInn}) дебиторки в ответе нет
          </p>
        ) : (
          <>
            {shown.map((org) => (
              <OrgBlock
                key={org.org_inn}
                org={org}
                orgs={orgs}
                withHeading={orgInn === ALL_ORGS}
              />
            ))}
            <p className="text-xs text-muted-foreground">{receivables.note}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
